/**
 * Approximate single pass transmission for better transparent glass rendering.
 * @module
 */
import { safeShaderReplace, type ShaderProgramLike } from "../shaderUtils";

const FRESNEL_FADEOFF_BASE = "1.5"; // Should be above 1.0. Larger values make the frensel effect fade out faster depending on the angle of the surface. 2.0 is physical.
const LINEAR_FRESNEL_FACTOR = "1.0"; // Should be >= 0.0. How much to the fresnel value directly affects the material opacity, even if the reflected content is dark.
const OPACITY_CAP = "0.6"; // Should be <= 1.0. This limits how opaque the reflections off the glass can be when viewed head-on.
const OPACITY_CAP_GLANCING_ANGLES = "1.0"; // Should be <= 1.0. This limits how opaque the reflections off the glass can be at glancing angles.

export function includeApproximateTransmission(shader: ShaderProgramLike) {
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <tonemapping_fragment>",
    `float dotNV = saturate(dot(geometryNormal, geometryViewDir));
      float fresnel = pow(${FRESNEL_FADEOFF_BASE}, (-5.55473 * dotNV - 6.98316) * dotNV);
      float weightedDiffuse = diffuseColor.r * 0.2 + diffuseColor.g * 0.3 + diffuseColor.b * 0.5;
      weightedDiffuse *= 1.0 - fresnel * 0.5; // Simulate that a thin sheet of glass appears thicker as it is viewed at an angle, so less light is transmitted through.
      float weightedColor = gl_FragColor.r * 0.2 + gl_FragColor.g * 0.3 + gl_FragColor.b * 0.5;
      float opacityMul = (fresnel * 2.5 + 1.5) * (weightedColor * 1.2 - weightedDiffuse) + fresnel * ${LINEAR_FRESNEL_FACTOR} + 1.0;
      gl_FragColor = vec4( gl_FragColor.rgb, clamp(opacityMul * diffuseColor.a, 0.0, mix(${OPACITY_CAP}, ${OPACITY_CAP_GLANCING_ANGLES}, saturate(fresnel * 1.2))));
      // Note that we don't include tonemapping for glass!`,
    "includeApproximateTransmission (tonemapping_fragment)",
  );
}
